import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  Layers,
  Target,
  Lightbulb,
  Palette,
  BarChart3,
  ArrowRight,
  Sparkles,
  Zap,
  Globe,
  Star
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import Process from "../components/Process";
import BrandEssenceGame from "../components/BrandEssenceGame";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";

const brandingSteps = [
  {
    step: "01",
    title: "Discovery & Strategy",
    desc: "We deep dive into your market, competitors, and core values to find the unique angle that makes your brand undeniable.",
    image: "https://images.unsplash.com/photo-1557838923-2985c318be48?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "02",
    title: "Visual Identity",
    desc: "Crafting the visual language—from logos and typography to color systems—that tells your story at a glance.",
    image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "Brand Voice",
    desc: "Defining how your brand speaks across every platform, ensuring a consistent and compelling personality.",
    image: "https://images.unsplash.com/photo-1493612276216-ee3925520721?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "Implementation",
    desc: "Deploying your new identity across digital and physical touchpoints for a seamless and impactful launch.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
  }
];

const Branding = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.4
    });

    let raf;
    const tick = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    window.lenis = lenis;
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.lenis = null;
    };
  }, []);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO
        title="Brand Engineering & Design"
        description="Transform your identity with Meraki Solutions. We specialize in visual identity, brand strategy, and creative direction to build digital legacies."
      />
      {/* <CustomCursor /> */}
      <div className="grain" />
      <Navbar />

      <main>
        <SectionStepper
          sections={[
            { id: "top", label: "Branding" },
            { id: "partners", label: "Partners" },
            { id: "capabilities", label: "Capabilities" },
            { id: "process", label: "Process" },
            { id: "game", label: "Essence Game" }
          ]}
        />
        {/* Hero Section */}
        <section id="top" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.55 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="w-full h-full"
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/7598768/7598768-hd_1920_1080_30fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 text-left px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Elevating Identity
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Engineering <br />
                <span className="italic font-serif-accent gold-text">Timeless</span> Brands
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                We don't just design logos; we build digital legacies. Meraki Solutions combines cinematic storytelling with technical precision to craft identities that resonate.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Start Your Legacy <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <Link to="/services" className="btn-ghost">
                  Explore Services
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Floating elements */}
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-10 w-24 h-24 rounded-full border border-[var(--rule)] opacity-20 hidden lg:block"
          />
          <motion.div
            animate={{ y: [0, 20, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-1/4 right-10 w-32 h-32 rounded-full border border-[var(--rule)] opacity-20 hidden lg:block"
          />
        </section>

        {/* Client Logos Section (Infinite Carousel) */}
        <section id="partners" className="py-32 bg-[var(--ink-2)] overflow-hidden border-y border-[var(--rule)]">
          <div className="container-xl px-6 mb-16">
            <Kicker index="01" label="Partnerships" />
            <h2 className="mt-6 font-display text-[clamp(36px,5vw,72px)] leading-[1.05] text-[var(--cream)]">
              Trusted by <span className="italic gold-text">Industry Leaders</span>
            </h2>
          </div>

          <div className="flex flex-col gap-12 md:mt-6">
            <LogoRow direction="left" speed={40} rowIdx={0} />
            <LogoRow direction="right" speed={35} rowIdx={1} />
            <LogoRow direction="left" speed={45} rowIdx={2} />
          </div>
        </section>

        {/* Services Overview Section */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <div className="flex flex-col mb-20">
              <Kicker index="02" label="Our Capabilities" />
              <h2 className="mt-8 font-display text-[clamp(32px,5.5vw,80px)] leading-[1.1] text-[var(--cream)] balance">
                Holistic Branding <span className="italic gold-text">Solutions</span>
              </h2>
              <p className="mt-8 text-[var(--cream-muted)] text-lg font-body max-w-2xl leading-relaxed">
                A brand is the sum of every interaction. We ensure every touchpoint is intentional, beautiful, and strategically aligned with your vision.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <Layers className="w-8 h-8" />,
                  title: "Visual Identity",
                  desc: "Logo design, color palettes, typography, and visual systems that define your unique aesthetic.",
                  image: "/images/branding/visual_identity.png"
                },
                {
                  icon: <Target className="w-8 h-8" />,
                  title: "Brand Strategy",
                  desc: "Market positioning, audience personas, and value propositions that drive growth.",
                  image: "/images/branding/strategy.png"
                },
                {
                  icon: <Lightbulb className="w-8 h-8" />,
                  title: "Creative Direction",
                  desc: "Guiding the visual and narrative flow of your brand across all digital and physical platforms.",
                  image: "/images/branding/creative_direction.png"
                },
                {
                  icon: <Palette className="w-8 h-8" />,
                  title: "Brand Guidelines",
                  desc: "Comprehensive documentation to ensure consistency across every application of your brand.",
                  image: "/images/branding/guidelines.png"
                },
                {
                  icon: <Globe className="w-8 h-8" />,
                  title: "Digital Presence",
                  desc: "Transforming your identity into high-performance websites and social experiences.",
                  image: "/images/branding/digital_presence.png"
                },
                {
                  icon: <BarChart3 className="w-8 h-8" />,
                  title: "Brand Audit",
                  desc: "Evaluating your current brand impact and identifying opportunities for evolution.",
                  image: "/images/branding/audit.png"
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative p-10 rounded-2xl overflow-hidden bg-[var(--ink-2)] border border-[var(--rule)] hover:border-[var(--gold)] transition-all duration-500 group min-h-[320px] flex flex-col justify-end"
                >
                  {/* Background Image with Hover Effect */}
                  <div
                    className="absolute inset-0 z-0 transition-all duration-700 grayscale group-hover:grayscale-0 opacity-20 group-hover:opacity-40 scale-105 group-hover:scale-100"
                    style={{
                      backgroundImage: `url(${service.image})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center"
                    }}
                  />

                  {/* Overlay for legibility */}
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/40 to-transparent" />

                  <div className="relative z-20">
                    <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform duration-500 origin-left">
                      {service.icon}
                    </div>
                    <h3 className="font-display text-2xl mb-4 text-[var(--cream)]">{service.title}</h3>
                    <p className="text-[var(--muted)] font-body leading-relaxed group-hover:text-[var(--cream-muted)] transition-colors duration-500">
                      {service.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <Process
          steps={brandingSteps}
          title="Our Branding Process"
          kicker={{ index: "03", label: "The Blueprint" }}
        />

        <BrandEssenceGame id="game" />

      </main>
      <Footer />
    </div>
  );
};

const LogoRow = ({ direction = "left", speed = 30, rowIdx = 0 }) => {
  const logos = [
    "1_20230304_202303_0000.png",
    "20230202_195113_0000.png",
    "20230507_184007_0001.png",
    "APEX LOGO.png",
    "Adah Logo Curve.png",
    "CAKE 21.png",
    "Gaagar Logo PNG.png",
    "Hair & Beauty salon Logo PNG.png",
    "IMG-20221215-WA0022.jpg",
    "LOGO1.png",
    "Mirana-black.jpg",
    "/images/branding/vowes.jpg",
    "/images/branding/writeonwalls.jpg",
    "/images/branding/skincity.jpg",
    "/images/branding/sajanvat.jpg",
    "/images/branding/abode.jpg",
    "Sanvi_logo.png",
    "Screenshot_2023-12-19-12-00-19-824_com.miui.gallery-edit.jpg",
    "UBItech_logo_final.jpg",
    "Webnovel logo.png",
    "belleame-logo.jpg",
    "ei_1609150829144-removebg-preview.png",
    "ifi final logo NEW.jpg",
    "logo.png",
    "mail.jpeg"
  ];

  // Distribute logos into 3 rows
  const itemsPerRow = Math.ceil(logos.length / 3);
  const rowLogos = logos.slice(rowIdx * itemsPerRow, (rowIdx + 1) * itemsPerRow);

  // Duplicate for infinite scroll
  const duplicatedLogos = [...rowLogos, ...rowLogos, ...rowLogos];

  return (
    <div className="relative flex overflow-hidden py-4">
      <motion.div
        animate={{
          x: direction === "left" ? ["0%", "-33.33%"] : ["-33.33%", "0%"]
        }}
        transition={{
          duration: speed,
          repeat: Infinity,
          ease: "linear"
        }}
        className="flex whitespace-nowrap gap-16 md:gap-24 px-8"
      >
        {duplicatedLogos.map((logo, idx) => (
          <div
            key={`${logo}-${idx}`}
            className="flex-shrink-0 w-32 h-16 md:w-48 md:h-24 flex items-center justify-center grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-500 transform hover:scale-110"
          >
            <img
              src={logo.startsWith("/") ? logo : `/images/clients_logo/${logo}`}
              alt="Client Logo"
              className="max-w-full max-h-full object-contain"
              loading="lazy"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default Branding;
